import React, { useEffect, useState, useRef } from "react";
import { Stack, Typography, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowBack, MessageRounded, Send } from "@mui/icons-material";
import { CustomInput } from "../../Components/Common/CustomInput";
import { CustomBtn } from "../../Components/Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";

const newSocket = io(process.env.REACT_APP_STAGING_API_URL);
// const newSocket = io("http://localhost:3000/");

const Chat = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const chattingUser = state?.userData;
  const { userData } = useSelector((state) => state.user);

  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      const newMessage = {
        senderId: userData.id,
        receiverId: chattingUser.id,
        conversationId: conversation?.id || null,
        message: message,
      };

      newSocket.emit("sendMessage", newMessage); // ✅ Emit the message to the server
      setMessage(""); // ✅ Clear input
      scrollToBottom();
    } catch (err) {
      console.log(err);
    }
  };

  const fetchConversation = async () => {
    try {
      const conversationResponse = await axiosInstance.get(
        `/chat/fetchConversationByUserId/${userData.id}/${chattingUser.id}`
      );
      if (conversationResponse) {
        setConversation(conversationResponse.data.conversation);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchConversation();
  }, []);

  const fetchMessages = async () => {
    try {
      const messagesResponse = await axiosInstance.get(
        `/chat/getConversationMessages?conversationId=${conversation.id}`
      );
      if (messagesResponse) {
        setMessages(messagesResponse.data.messages);
        scrollToBottom();
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!newSocket) {
      return;
    }
    newSocket.emit("joinRoom", { conversationId: conversation?.id });

    newSocket.on("newMessage", (newMsg) => {
      setMessages((prev) => [...prev, newMsg]);
      scrollToBottom();
    });

    return () => {
      newSocket.off("newMessage");
      newSocket.off("joinRoom");
    };
  }, [conversation]);

  useEffect(() => {
    if (conversation?.id) {
      fetchMessages();
    }
  }, [conversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <Stack sx={{ p: 3, height: "100vh", gap: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ borderBottom: "1px solid #e0e0e0", pb: 2, gap: 2 }}
      >
        <IconButton>
          <ArrowBack
            onClick={() => navigate("/conversations")}
            sx={{ color: "#000" }}
          />
        </IconButton>
        <Typography variant="h5" sx={{ fontFamily: "Inter-Bold" }}>
          {chattingUser?.firstName}
        </Typography>
      </Stack>

      <Stack
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 2,
          backgroundColor: "#f9f9f9",
          borderRadius: 2,
        }}
      >
        {messages.length > 0 ? (
          messages.map((msg, index) => (
            <Typography
              key={index}
              sx={{
                textAlign: msg.senderId === userData.id ? "right" : "left",
                backgroundColor:
                  msg.senderId === userData.id ? "#DCF8C6" : "#FFFFFF",
                padding: "10px",
                borderRadius: "10px",
                maxWidth: "75%",
                alignSelf:
                  msg.senderId === userData.id ? "flex-end" : "flex-start",
                marginBottom: "8px",
              }}
            >
              {msg.message}
            </Typography>
          ))
        ) : (
          <Typography sx={{ textAlign: "center", color: "#999" }}>
            No messages yet.
          </Typography>
        )}
        <div ref={messagesEndRef} />
      </Stack>

      <Stack direction="row" gap={1} alignItems="center">
        <CustomInput
          value={message}
          setValue={setMessage}
          Icon={MessageRounded}
          placeholder="Write a message..."
          mT={2}
          leftIcon
        />
        <CustomBtn
          text=""
          Icon={Send}
          width="auto"
          onClick={handleSendMessage}
        />
      </Stack>
    </Stack>
  );
};

export default Chat;
